import { ConnectorNotFoundError } from 'wagmi';
import type { Chain } from 'wagmi';
import { InjectedConnector } from 'wagmi/connectors/injected';

export type SafepalConnectorOptions = Pick<
  any,
  'shimChainChangedDisconnect' | 'shimDisconnect'
> & {
  UNSTABLE_shimOnConnectSelectAccount?: boolean;
};

export class SafepalConnector extends InjectedConnector {
  readonly id = 'safepal';
  readonly ready =
    typeof window != 'undefined' && !!this.#findProvider(window.ethereum);

  #provider?: Window['ethereum'];
  #UNSTABLE_shimOnConnectSelectAccount: SafepalConnectorOptions['UNSTABLE_shimOnConnectSelectAccount'];

  constructor({
    chains,
    options: options_,
  }: {
    chains?: Chain[];
    options?: SafepalConnectorOptions;
  } = {}) {
    const options = {
      name: 'Safepal',
      shimDisconnect: true,
      shimChainChangedDisconnect: true,
      ...options_,
    };
    super({ chains, options });

    this.#UNSTABLE_shimOnConnectSelectAccount =
      options.UNSTABLE_shimOnConnectSelectAccount;
  }

  async connect({ chainId }: { chainId?: number } = {}) {
    try {
      const provider = await this.getProvider();
      if (!provider) throw new ConnectorNotFoundError();

      if (provider.on) {
        provider.on('accountsChanged', this.onAccountsChanged);
        provider.on('chainChanged', this.onChainChanged);
        provider.on('disconnect', this.onDisconnect);
      }

      this.emit('message', { type: 'connecting' });

      if (
        this.#UNSTABLE_shimOnConnectSelectAccount &&
        this.options?.shimDisconnect
      ) {
        const accounts = await provider
          .request({
            method: 'eth_accounts',
          })
          .catch(() => []);
        const isConnected = !!accounts[0];
        if (isConnected)
          await provider.request({
            method: 'wallet_requestPermissions',
            params: [{ eth_accounts: {} }],
          });
      }

      const account = await this.getAccount();
      // Switch to chain if provided
      let id = await this.getChainId();
      let unsupported = this.isChainUnsupported(id);
      if (chainId && id !== chainId) {
        const chain = await this.switchChain(chainId);
        id = chain.id;
        unsupported = this.isChainUnsupported(id);
      }

      return { account, chain: { id, unsupported }, provider };
    } catch (error) {
      throw error;
    }
  }

  async getProvider() {
    if (typeof window !== 'undefined') {
      this.#provider = this.#findProvider(window.ethereum);
    }
    return this.#provider;
  }

  #getReady(ethereum?: any) {
    const isSafepal = !!ethereum?.isSafePal;
    if (!isSafepal) return;
    return ethereum;
  }

  #findProvider(ethereum?: any) {
    return this.#getReady(ethereum);
  }
}
